import { RSAA } from 'redux-api-middleware';
import customFailure from 'helpers/customFailureAction';
import { shape, arrayOf, string, bool, instanceOf } from 'prop-types';
import { Dispatch } from 'redux';
import { TDispatchResult, TGetRootState } from 'types/shared';

export const GET_GLOSSARY_REQUEST = 'opus/glossary/GET_GLOSSARY_REQUEST';
export const GET_GLOSSARY_SUCCESS = 'opus/glossary/GET_GLOSSARY_SUCCESS';
export const GET_GLOSSARY_FAILURE = 'opus/glossary/GET_GLOSSARY_FAILURE';

export const SET_GLOSSARY_REQUEST = 'opus/glossary/SET_GLOSSARY_REQUEST';
export const SET_GLOSSARY_SUCCESS = 'opus/glossary/SET_GLOSSARY_SUCCESS';
export const SET_GLOSSARY_FAILURE = 'opus/glossary/SET_GLOSSARY_FAILURE';

const endpoint = '/kv/glossary';

type GlossaryActionTypes =
  | typeof GET_GLOSSARY_REQUEST
  | typeof GET_GLOSSARY_SUCCESS
  | typeof GET_GLOSSARY_FAILURE
  | typeof SET_GLOSSARY_REQUEST
  | typeof SET_GLOSSARY_SUCCESS
  | typeof SET_GLOSSARY_FAILURE;

interface IGlossaryAction {
  type: GlossaryActionTypes;
  payload: any;
}

interface IGlossaryState {
  loading: boolean;
  success: boolean;
  error: Error | null;
  errorInfo: any;
  data: any;
}

export const initialState = {
  loading: false,
  success: false,
  error: null,
  errorInfo: null,
  data: null,
};

export default function reducer(
  state: IGlossaryState = initialState,
  action: IGlossaryAction
): IGlossaryState {
  switch (action.type) {
    case GET_GLOSSARY_REQUEST:
    case SET_GLOSSARY_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
        errorInfo: null,
      };

    case GET_GLOSSARY_SUCCESS:
    case SET_GLOSSARY_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        error: null,
        errorInfo: null,
        data: action.payload,
      };

    case GET_GLOSSARY_FAILURE:
    case SET_GLOSSARY_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: new Error(action.payload.error),
        errorInfo: action.payload.errorInfo,
      };

    default:
      return state;
  }
}

/**
 * Retrieve the selected message from the API and saves it to the store.
 */
export function loadGlossary(): any {
  return {
    [RSAA]: {
      types: [
        GET_GLOSSARY_REQUEST,
        GET_GLOSSARY_SUCCESS,
        {
          type: GET_GLOSSARY_FAILURE,
          payload: async (
            action: IGlossaryAction,
            state: IGlossaryState,
            res: any
          ): Promise<any> => await customFailure(res),
        },
      ],
      endpoint,
      method: 'GET',
      credentials: 'same-origin',
    },
  };
}

export function shouldLoadGlossary(): TDispatchResult<boolean> {
  return (dispatch: Dispatch, getState: TGetRootState): boolean => {
    const {
      glossary: { loading, success },
    } = getState();
    return !loading && !success;
  };
}

/**
 * Sends the selected message to the API and saves it to the store.
 */
export const setGlossary = (data: any): any => {
  const endpoint = '/kv/glossary';

  return {
    [RSAA]: {
      types: [
        SET_GLOSSARY_REQUEST,
        {
          type: SET_GLOSSARY_SUCCESS,
          payload: (): any => data,
        },
        {
          type: SET_GLOSSARY_FAILURE,
          payload: async (
            action: IGlossaryAction,
            state: IGlossaryState,
            res: any
          ): Promise<any> => await customFailure(res),
        },
      ],
      endpoint,
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    },
  };
};

export const glossaryPropType = shape({
  loading: bool.isRequired,
  success: bool.isRequired,
  error: instanceOf(Error),
  errorInfo: string,
  data: arrayOf(
    shape({
      name: string.isRequired,
      slug: string.isRequired,
      topics: arrayOf(
        shape({
          name: string.isRequired,
          slug: string.isRequired,
          content: string.isRequired,
        })
      ),
    })
  ),
});
